/**
 * fleet.ts — the chassis catalog + component math of the space RPG (pure).
 *
 * Chassis flight stats are CLIENT data (the sector-layout law); the server
 * mirrors only costs + rank gates (server/src/lib/constants.ts FLEET_CHASSIS
 * — keep the two tables' costSov/rank columns identical). Component items
 * (`comp_<slot>` playerItems, quality 0–1000) multiply the base stats;
 * `shipStats` derives the flown numbers from chassis + equipped components.
 */

export interface ChassisSpec {
  key: string;
  name: string;
  role: string;
  prefab: string;        // prop:<key> — falls back to the stock fighter hull until cooked
  costSov: number;       // MIRROR of server FLEET_CHASSIS
  rank: number;          // MIRROR of server FLEET_CHASSIS
  hullSize: number;      // rendered length (m)
  maxSpeed: number;      // m/s at 100% throttle
  hp: number;            // hull integrity
  shield: number;        // shield pool
  capacitor: number;     // weapon energy pool
  boost: number;         // booster reservoir multiplier
  cargo: number;         // cargo units (mining/hauling hooks)
  turnRate: number;      // steering multiplier (interceptors dance, gunships lumber)
  /** The Sovereign carries a walkable deck + a dorsal turret station. */
  walkable?: boolean;
}

export const CHASSIS: Record<string, ChassisSpec> = {
  ship_lancer: {
    key: 'ship_lancer', name: 'Lancer', role: 'strike fighter', prefab: 'prop:ship_lancer',
    costSov: 0, rank: 0, hullSize: 9, maxSpeed: 105, hp: 100, shield: 60, capacitor: 100,
    boost: 1, cargo: 4, turnRate: 1,
  },
  ship_talon: {
    key: 'ship_talon', name: 'Talon', role: 'interceptor', prefab: 'prop:ship_talon',
    costSov: 1200, rank: 1, hullSize: 8, maxSpeed: 132, hp: 70, shield: 45, capacitor: 85,
    boost: 1.25, cargo: 2, turnRate: 1.3,
  },
  ship_courier: {
    key: 'ship_courier', name: 'Courier', role: 'cargo runner', prefab: 'prop:ship_courier',
    costSov: 1800, rank: 1, hullSize: 12, maxSpeed: 96, hp: 120, shield: 70, capacitor: 80,
    boost: 0.9, cargo: 24, turnRate: 0.8,
  },
  ship_bulwark: {
    key: 'ship_bulwark', name: 'Bulwark', role: 'heavy gunship', prefab: 'prop:ship_bulwark',
    costSov: 3500, rank: 2, hullSize: 13, maxSpeed: 84, hp: 220, shield: 130, capacitor: 150,
    boost: 0.8, cargo: 8, turnRate: 0.62,
  },
  ship_sovereign: {
    key: 'ship_sovereign', name: 'Sovereign', role: 'personal cruiser', prefab: 'prop:ship_sovereign',
    costSov: 9000, rank: 3, hullSize: 26, maxSpeed: 78, hp: 380, shield: 220, capacitor: 200,
    boost: 0.7, cargo: 40, turnRate: 0.45, walkable: true,
  },
};
export const DEFAULT_CHASSIS = 'ship_lancer';

export const SLOTS = ['reactor', 'engine', 'capacitor', 'shield', 'armor', 'weapon', 'missile', 'counter', 'droidport', 'cargo'] as const;
export type FleetSlot = (typeof SLOTS)[number];

export const SLOT_LABEL: Record<FleetSlot, string> = {
  reactor: 'Reactor', engine: 'Engine', capacitor: 'Capacitor', shield: 'Shield Emitter',
  armor: 'Hull Plating', weapon: 'Cannons', missile: 'Missile Rack', counter: 'Countermeasures',
  droidport: 'Droid Port', cargo: 'Cargo Hold',
};

export interface EquippedComp { slot: string; itemKey: string; quality: number }

export interface ShipStats {
  maxSpeed: number; hp: number; shield: number; capacitor: number;
  boost: number; cargo: number; turnRate: number;
  /** Weapon damage/cooldown multipliers (cannons comp). */
  weaponMult: number; fireCdMult: number;
  /** Missile salvo count per rearm (0 = no rack equipped). */
  missiles: number;
  /** Countermeasure charges per rearm (0 = none). */
  counters: number;
  /** Droid program strength 0..1 (0 = no droid docked). */
  droid: number;
  /** Shield/capacitor regen multipliers (reactor). */
  regenMult: number;
}

/** q 0–1000 → a multiplier 1..(1+span). */
const qMult = (q: number, span: number): number => 1 + Math.max(0, Math.min(1000, q)) / 1000 * span;

/** The three flight-stat skills (Space Wave 1 — the catalogue's promises,
 *  kept): Navigation +3 % speed, Shield Systems +5 % pool, Gunnery +4 %
 *  damage, per level 0–5. */
export interface SkillLevels { gunnery: number; shields: number; navigation: number }
export const NO_SKILLS: SkillLevels = { gunnery: 0, shields: 0, navigation: 0 };
/** A live wormhole's effect on its endpoint systems (shared/space_industry
 *  systemEffectOf): pulsar shields ×1.25, magnetar damage ×1.2, cataclysm
 *  capacitor ×0.75. */
export type SystemEffect = '' | 'pulsar' | 'magnetar' | 'cataclysm';

/** Derived flight/combat stats for a chassis + its equipped components,
 *  the pilot's skills and the system's environment. */
export function shipStats(chassisKey: string, comps: EquippedComp[], skills: SkillLevels = NO_SKILLS, env: { effect: SystemEffect } = { effect: '' }): ShipStats {
  const c = CHASSIS[chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
  const q = (slot: string): number => comps.find((x) => x.slot === slot)?.quality ?? -1;
  const has = (slot: string): boolean => q(slot) >= 0;
  const stats: ShipStats = {
    maxSpeed: c.maxSpeed * (has('engine') ? qMult(q('engine'), 0.35) : 1),
    hp: Math.round(c.hp * (has('armor') ? qMult(q('armor'), 0.6) : 1)),
    shield: Math.round(c.shield * (has('shield') ? qMult(q('shield'), 0.8) : 1)),
    capacitor: Math.round(c.capacitor * (has('capacitor') ? qMult(q('capacitor'), 0.7) : 1)),
    boost: c.boost,
    cargo: c.cargo + (has('cargo') ? Math.round(q(('cargo')) / 1000 * 20) : 0),
    turnRate: c.turnRate,
    weaponMult: has('weapon') ? qMult(q('weapon'), 0.9) : 1,
    fireCdMult: has('weapon') ? 1 / qMult(q('weapon'), 0.35) : 1,
    missiles: has('missile') ? 2 + Math.floor(q('missile') / 250) : 0,
    counters: has('counter') ? 2 + Math.floor(q('counter') / 300) : 0,
    droid: has('droidport') ? Math.max(0.25, q('droidport') / 1000) : 0,
    regenMult: has('reactor') ? qMult(q('reactor'), 0.6) : 1,
  };
  // Pilot skills (Space Wave 1).
  stats.maxSpeed *= 1 + 0.03 * skills.navigation;
  stats.shield = Math.round(stats.shield * (1 + 0.05 * skills.shields));
  stats.weaponMult *= 1 + 0.04 * skills.gunnery;
  // The system's wormhole effect while a live hole touches it.
  if (env.effect === 'pulsar') stats.shield = Math.round(stats.shield * 1.25);
  else if (env.effect === 'magnetar') stats.weaponMult *= 1.2;
  else if (env.effect === 'cataclysm') stats.capacitor = Math.round(stats.capacitor * 0.75);
  return stats;
}

/** Component item keys → the slot they occupy (droids dock at the droid port). */
export function slotOfItem(itemKey: string): FleetSlot | null {
  if (itemKey.startsWith('droid_')) return 'droidport';
  if (!itemKey.startsWith('comp_')) return null;
  const s = itemKey.slice(5) as FleetSlot;
  return (SLOTS as readonly string[]).includes(s) ? s : null;
}
